/**
 * customerBackfill — fills in MISSING fields on the signed-in customer's
 * `customers` row (and mirrors basic info into `customer_prefs`) using whatever
 * the visitor typed at checkout / enquiry.
 *
 * SAFEGUARDS:
 *  - Never overwrites any non-empty field on the existing profile.
 *  - Trims input and ignores blank/whitespace-only values.
 *  - Returns a status object so the caller can show a small UI indicator.
 */
import { supabase } from "@/integrations/supabase/client";

interface BackfillInput {
  userId: string;
  email?: string | null;
  fullName?: string | null;
  phoneNumber?: string | null;
  location?: string | null;
}

export interface BackfillResult {
  ok: boolean;
  /** Human-readable field labels that were written for the first time. */
  written: string[];
  /** Field labels that were provided but kept as-is (already had a value). */
  preserved: string[];
  error?: string;
}

const clean = (s?: string | null): string => (typeof s === "string" ? s.trim() : "");

const FIELD_LABELS: Record<string, string> = {
  full_name: "name",
  phone_number: "phone",
  location: "location",
  email: "email",
};

export async function backfillCustomerProfile(input: BackfillInput): Promise<BackfillResult> {
  const userId = input.userId;
  if (!userId) return { ok: false, written: [], preserved: [], error: "no-user" };

  const fullName = clean(input.fullName);
  const phoneNumber = clean(input.phoneNumber);
  const location = clean(input.location);
  const email = clean(input.email);

  const written: string[] = [];
  const preserved: string[] = [];

  try {
    const { data: existing } = await supabase
      .from("customers")
      .select("full_name, phone_number, location, email")
      .eq("user_id", userId)
      .maybeSingle();

    const patch: Record<string, string> = {};
    const consider = (key: keyof typeof FIELD_LABELS, incoming: string) => {
      if (!incoming) return;
      const current = clean((existing as any)?.[key]);
      if (current) { preserved.push(FIELD_LABELS[key]); return; }
      patch[key] = incoming;
      written.push(FIELD_LABELS[key]);
    };
    consider("full_name", fullName);
    consider("phone_number", phoneNumber);
    consider("location", location);
    consider("email", email);

    if (Object.keys(patch).length > 0) {
      if (!existing) {
        await supabase.from("customers").insert({ user_id: userId, email: email || patch.email || "", ...patch });
      } else {
        await supabase.from("customers").update(patch).eq("user_id", userId);
      }
    }

    // Mirror returning-info into customer_prefs so it roams across devices.
    if (fullName || phoneNumber || location) {
      const { data: prefs } = await supabase
        .from("customer_prefs")
        .select("data")
        .eq("customer_user_id", userId)
        .maybeSingle();
      const current = ((prefs as any)?.data as Record<string, unknown>) || {};
      const returningInfo = (current.returningInfo as Record<string, unknown>) || {};
      const merged = {
        ...returningInfo,
        ...(fullName && !returningInfo.name ? { name: fullName } : {}),
        ...(phoneNumber && !returningInfo.phone ? { phone: phoneNumber } : {}),
        ...(location && !returningInfo.location ? { location } : {}),
        savedAt: Date.now(),
      };
      const { getCurrentTenantId } = await import("@/lib/tenantContext");
      await supabase.from("customer_prefs").upsert(
        { customer_user_id: userId, tenant_id: getCurrentTenantId(), data: { ...current, returningInfo: merged } as any },
        { onConflict: "customer_user_id" }
      );
    }

    return { ok: true, written, preserved };
  } catch (e: any) {
    console.warn("backfillCustomerProfile failed", e);
    return { ok: false, written, preserved, error: e?.message || String(e) };
  }
}
